import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Stack,
  TextField,
  Pagination,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Refresh,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { examinationAPI, branchAPI, courseAPI, batchAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ApprovedStudents = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedExamination, setSelectedExamination] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Data
  const [approvedStudents, setApprovedStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [examinations, setExaminations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchBranches();
    }
    fetchExaminations();
    fetchCourses();
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchApprovedStudents();
  }, [selectedBranch, selectedExamination, selectedCourse, selectedBatch, search, startDate, endDate, page]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchExaminations = async () => {
    try {
      const response = await examinationAPI.getAll();
      setExaminations(response.data.data || response.data.examinations || []);
    } catch (error) {
      console.error('Error fetching examinations:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchApprovedStudents = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };

      if (selectedBranch) params.branch = selectedBranch;
      if (selectedExamination) params.examination = selectedExamination;
      if (selectedCourse) params.course = selectedCourse;
      if (selectedBatch) params.batch = selectedBatch;
      if (search) params.search = search;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await examinationAPI.getApprovedStudentExams(params);

      setApprovedStudents(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching approved students:', error);
      toast.error('Failed to load approved students');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadHallTicket = async (studentExam) => {
    try {
      const response = await examinationAPI.downloadHallTicket(studentExam._id);

      // Create blob URL and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hall-ticket-${studentExam.hallTicketNumber || studentExam._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Hall ticket downloaded successfully');
    } catch (error) {
      console.error('Error downloading hall ticket:', error);
      toast.error('Failed to download hall ticket');
    }
  };

  const clearFilters = () => {
    setSelectedBranch('');
    setSelectedExamination('');
    setSelectedCourse('');
    setSelectedBatch('');
    setSearch('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/examinations')}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Approved Students
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and download hall tickets for approved students
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchApprovedStudents}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={3}>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  onChange={(e) => {
                    setSelectedBranch(e.target.value);
                    setPage(1);
                  }}
                  label="Branch"
                  MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExamination}
                onChange={(e) => {
                  setSelectedExamination(e.target.value);
                  setPage(1);
                }}
                label="Examination"
              >
                <MenuItem value="">All Examinations</MenuItem>
                {examinations.map((exam) => (
                  <MenuItem key={exam._id} value={exam._id}>
                    {exam.name} ({exam.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Course</InputLabel>
              <Select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setPage(1);
                }}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((course) => (
                  <MenuItem key={course._id} value={course._id}>
                    {course.name} ({course.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Batch</InputLabel>
              <Select
                value={selectedBatch}
                onChange={(e) => {
                  setSelectedBatch(e.target.value);
                  setPage(1);
                }}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name, Form Number or Hall Ticket No."
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Approved Students Table */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Approved Students ({totalCount})
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : approvedStudents.length > 0 ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Hall Ticket No.</strong></TableCell>
                    <TableCell><strong>Roll No.</strong></TableCell>
                    <TableCell><strong>Register No.</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    {isSuperAdmin && <TableCell><strong>Branch</strong></TableCell>}
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Examination</strong></TableCell>
                    <TableCell><strong>Approved On</strong></TableCell>
                    <TableCell><strong>Approved By</strong></TableCell>
                    <TableCell align="center"><strong>Hall Ticket</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedStudents.map((studentExam) => (
                    <TableRow key={studentExam._id} hover>
                      <TableCell>
                        <Chip
                          label={studentExam.hallTicketNumber || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={studentExam.rollNumber || 'N/A'}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{studentExam.student?.formNumber || 'N/A'}</TableCell>
                      <TableCell>{studentExam.student?.personalInfo?.nameAsPerTC || 'N/A'}</TableCell>
                      {isSuperAdmin && <TableCell>{studentExam.branch?.name || 'N/A'}</TableCell>}
                      <TableCell>{studentExam.student?.course?.name || 'N/A'}</TableCell>
                      <TableCell>{studentExam.examination?.name || 'N/A'}</TableCell>
                      <TableCell>
                        {studentExam.approvedAt
                          ? new Date(studentExam.approvedAt).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{studentExam.approvedBy?.name || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<Download />}
                          onClick={() => handleDownloadHallTicket(studentExam)}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              No approved students found. Try adjusting your filters.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ApprovedStudents;
